import { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Send, Sparkles, Trash2, Wand2, X } from "lucide-react";
import { api, formatBytes, type Account, type ComposeKind, type Draft, type DraftUpload } from "../api";
import { Select } from "./Select";
import { AdressFeld, istGueltigeAdresse } from "./AdressFeld";
import { useDialogFokus } from "../lib/dialog";
import { toast, fehlerText } from "./ui/Toast";
import { bestaetigen } from "./ui/Bestaetigen";
import { Ursprung } from "./Ursprung";

interface Props {
  messageId: number | null;
  draftId?: number;
  kind?: ComposeKind;
  onClose: () => void;
  onSent: () => void;
}

interface Anhang {
  messageId: number;
  index: number;
  filename: string;
  size: number;
}

const TITEL: Record<ComposeKind, string> = {
  antwort: "Antwort",
  allen: "Antwort an alle",
  weiterleiten: "Weiterleiten",
  neu: "Neue Nachricht",
};

/** Häufige Änderungswünsche: ein Tipp genügt. */
const SCHNELL: [string, string][] = [
  ["Kürzer", "Deutlich kürzer, nur das Wesentliche."],
  ["Ausführlicher", "Etwas ausführlicher und erklärender."],
  ["Förmlicher", "Förmlicher, mit Sie."],
  ["Lockerer", "Lockerer und persönlicher, mit du."],
  ["Freundlicher", "Wärmer und freundlicher im Ton."],
  ["Bestimmter", "Klarer und bestimmter, ohne unhöflich zu sein."],
  ["Englisch", "Auf Englisch."],
];

/** Wie lange nach der letzten Änderung der Entwurf von selbst gesichert wird. */
const AUTOSICHERN_MS = 1500;

function jsonListe<T>(text: string | null | undefined): T[] {
  try {
    const v = JSON.parse(text ?? "[]") as unknown;
    return Array.isArray(v) ? (v as T[]) : [];
  } catch {
    return [];
  }
}

export function Composer({ messageId, draftId, kind = "antwort", onClose, onSent }: Props) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const [uploads, setUploads] = useState<DraftUpload[]>([]);
  const [anhaengeMit, setAnhaengeMit] = useState(true);
  const [laedtHoch, setLaedtHoch] = useState<string | null>(null);
  const [zieht, setZieht] = useState(false);
  const [kopienOffen, setKopienOffen] = useState(false);
  const [zustand, setZustand] = useState<"laedt" | "bereit" | "sendet" | "schreibt">("laedt");
  const [fehler, setFehler] = useState<string | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [konten, setKonten] = useState<Account[]>([]);
  // Hat der Nutzer etwas geändert? Und wie steht es um die Sicherung?
  const [geaendert, setGeaendert] = useState(false);
  const [sicherung, setSicherung] = useState<"ruhe" | "sichert" | "gesichert" | "fehler">("ruhe");
  const version = useRef(0);

  const box = useRef<HTMLDivElement | null>(null);
  const textFeld = useRef<HTMLTextAreaElement | null>(null);
  const anFeld = useRef<HTMLInputElement | null>(null);
  const dateiWahl = useRef<HTMLInputElement | null>(null);
  const ziehZaehler = useRef(0);
  // In dieser Sitzung neu angelegt (nicht aus der Entwurfsliste geöffnet)?
  const neuAngelegt = useRef(false);
  const beendet = useRef(false);
  const druckDaneben = useRef(false);

  useEffect(() => { api.accounts().then(setKonten).catch(() => {}); }, []);

  /** Jede Nutzeränderung geht hier durch — sie stößt das automatische Sichern an. */
  const aendern = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setGeaendert(true);
    version.current++;
  };

  const uebernehmen = (d: Draft) => {
    setDraft(d);
    // Vom Agenten vorgeschrieben: Der Text steht, jetzt geht es ums Feinjustieren.
    if (d.source === "llm") {
      setModus("anpassen");
      setHilfeOffen(true);
    }
    setTo(jsonListe<string>(d.to_json));
    const kopie = jsonListe<string>(d.cc_json);
    const blind = jsonListe<string>(d.bcc_json);
    setCc(kopie);
    setBcc(blind);
    setSubject(d.subject ?? "");
    setBody(d.body ?? "");
    setAnhaenge(jsonListe<Anhang>(d.attachments_json));
    if (kopie.length > 0 || blind.length > 0) setKopienOffen(true);
    void api.uploads(d.id).then(setUploads).catch(() => {});
  };

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      setZustand("laedt");
      setLadeFehler(null);
      try {
        let id: number | undefined = draftId;
        let angelegt = false;
        if (!id && kind === "neu") {
          // Neue Mail ohne Bezug: leerer Entwurf mit Signatur.
          id = (await api.neuerEntwurf()).id;
          angelegt = true;
        } else if (!id && messageId) {
          // Entwurf aus der Nachricht bauen — ganz ohne Modell.
          id = (await api.compose(messageId, kind)).id;
          angelegt = true;
        }
        if (!id) throw new Error("Kein Entwurf angegeben.");
        // Inzwischen abgebrochen (Dialog zu, anderer Entwurf): den eben
        // angelegten nicht verwaist in der Liste stehen lassen.
        if (abgebrochen) {
          if (angelegt) void api.deleteDraft(id).catch(() => {});
          return;
        }
        neuAngelegt.current = angelegt;
        const geladen = (await api.drafts()).find((d) => d.id === id);
        if (!geladen) throw new Error("Der Entwurf existiert nicht mehr — vielleicht wurde er schon gesendet.");
        if (!abgebrochen) uebernehmen(geladen);
      } catch (err) {
        if (!abgebrochen) setLadeFehler(fehlerText(err));
      } finally {
        if (!abgebrochen) setZustand("bereit");
      }
    })();
    return () => { abgebrochen = true; };
  }, [messageId, draftId, kind]);

  // Nach dem Laden den Fokus dorthin, wo man als Nächstes tippt: ohne
  // Empfänger ins An-Feld, sonst an den Anfang des Textes über dem Zitat.
  useEffect(() => {
    if (zustand !== "bereit" || !draft) return;
    requestAnimationFrame(() => {
      if (to.length === 0) anFeld.current?.focus();
      else if (textFeld.current) {
        textFeld.current.focus();
        textFeld.current.setSelectionRange(0, 0);
        textFeld.current.scrollTop = 0;
      }
    });
    // Nur einmal nach dem Laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zustand === "bereit" && draft?.id]);

  const sichern = useCallback(async () => {
    if (!draft) return;
    const stand = version.current;
    setSicherung("sichert");
    try {
      await api.updateDraft(draft.id, {
        subject,
        body,
        to,
        cc,
        bcc,
        // Abgewählte Anhänge werden beim Speichern verworfen, nicht beim Senden —
        // so sieht man im Entwurf, was tatsächlich mitgeht.
        attachments: anhaengeMit ? anhaenge : [],
      });
      if (version.current === stand) setSicherung("gesichert");
    } catch (err) {
      setSicherung("fehler");
      throw err;
    }
  }, [draft, subject, body, to, cc, bcc, anhaenge, anhaengeMit]);

  // Automatisch sichern, kurz nachdem sich etwas geändert hat.
  useEffect(() => {
    if (!geaendert || !draft || zustand !== "bereit") return;
    const t = window.setTimeout(() => {
      if (!beendet.current) void sichern().catch(() => {});
    }, AUTOSICHERN_MS);
    return () => window.clearTimeout(t);
  }, [geaendert, draft, zustand, sichern]);

  // „Anhänge mitlesen": die letzte Wahl merken, Standard aus — Bilder gehen
  // ungeschwärzt an den Anbieter, das soll bewusst gewählt sein.
  const [anhaengeLesen, setAnhaengeLesen] = useState(() => {
    try { return localStorage.getItem("maildeck-anhaenge-lesen") === "1"; } catch { return false; }
  });
  const anhaengeUmschalten = (an: boolean) => {
    setAnhaengeLesen(an);
    try { localStorage.setItem("maildeck-anhaenge-lesen", an ? "1" : "0"); } catch { /* privater Modus */ }
  };

  // Stichpunkte fürs Formulieren. Das Feld bleibt nach dem Formulieren
  // stehen — so lässt sich nachschärfen und noch einmal formulieren.
  const [hilfeOffen, setHilfeOffen] = useState(false);
  const [stichpunkte, setStichpunkte] = useState("");
  // Anpassen: vorhandenen Text nach Hinweis überarbeiten. Formulieren: aus Stichpunkten schreiben.
  const [modus, setModus] = useState<"anpassen" | "formulieren">("formulieren");
  const [anpassHinweis, setAnpassHinweis] = useState("");
  const stichpunktFeld = useRef<HTMLTextAreaElement | null>(null);
  const istAntwort = Boolean(draft?.reply_to_id) && draft?.kind !== "weiterleiten";

  const hilfeUmschalten = () => {
    setHilfeOffen((offen) => {
      if (!offen) requestAnimationFrame(() => (modus === "anpassen" ? document.getElementById("anpassung") : stichpunktFeld.current)?.focus());
      return !offen;
    });
  };

  /**
   * Das Modell formuliert den Text — aus den Stichpunkten, aus dem, was oben
   * schon steht, oder bei Antworten auch ganz ohne Vorgabe. Signatur und Zitat
   * bleiben unangetastet.
   */
  const formulieren = async (art: "anpassen" | "formulieren" = modus, hinweis?: string) => {
    if (!draft || zustand !== "bereit") return;
    const vorgabe = art === "anpassen" ? (hinweis ?? anpassHinweis) : stichpunkte;
    if (art === "anpassen" && !vorgabe.trim()) {
      setFehler("Schreib kurz, was anders werden soll — oder tippe auf einen der Vorschläge.");
      return;
    }
    setZustand("schreibt");
    setFehler(null);
    const vorher = { body, subject };
    try {
      const ergebnis = await api.formulieren(draft.id, { stichpunkte: vorgabe, body, subject, to, anhaengeLesen: istAntwort && anhaengeLesen, modus: art });
      setBody(ergebnis.body);
      if (ergebnis.subject && !subject.trim()) setSubject(ergebnis.subject);
      setGeaendert(true);
      version.current++;
      const g = ergebnis.gelesen;
      const gelesen = g
        ? g.bilder.length + g.pdfs.length > 0
          ? ` Gelesen: ${[...g.bilder, ...g.pdfs].join(", ")}.`
          : " Keine lesbaren Bilder oder PDFs gefunden."
        : "";
      toast.info(`${art === "anpassen" ? "Angepasst" : "Formuliert"} von ${ergebnis.model} — bitte prüfen.${gelesen}`, {
        dauer: 8000,
        aktion: {
          label: "Rückgängig",
          onClick: () => {
            setBody(vorher.body);
            setSubject(vorher.subject);
            setGeaendert(true);
            version.current++;
          },
        },
      });
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setZustand("bereit");
    }
  };

  const dateienHochladen = async (dateien: File[]) => {
    if (!draft || dateien.length === 0) return;
    setFehler(null);
    const fehlgeschlagen: string[] = [];
    for (const datei of dateien) {
      setLaedtHoch(datei.name);
      try {
        const puffer = await datei.arrayBuffer();
        // In Blöcken kodieren: String.fromCharCode mit einer ganzen Datei
        // sprengt bei größeren Anhängen den Aufrufstapel.
        const bytes = new Uint8Array(puffer);
        let roh = "";
        for (let i = 0; i < bytes.length; i += 8192) roh += String.fromCharCode(...bytes.subarray(i, i + 8192));
        const neu = await api.addUpload(draft.id, datei.name, btoa(roh), datei.type || undefined);
        setUploads((v) => [...v, neu]);
        setGeaendert(true);
      } catch (err) {
        fehlgeschlagen.push(`${datei.name}: ${fehlerText(err)}`);
      }
    }
    setLaedtHoch(null);
    if (fehlgeschlagen.length > 0) setFehler(fehlgeschlagen.join("\n"));
  };

  const senden = async () => {
    if (!draft || zustand !== "bereit") return;
    const alle = [...to, ...cc, ...bcc];
    if (to.length === 0 && cc.length === 0 && bcc.length === 0) {
      setFehler("Ohne Empfänger geht nichts raus.");
      anFeld.current?.focus();
      return;
    }
    const falsch = alle.filter((a) => !istGueltigeAdresse(a));
    if (falsch.length > 0) {
      setFehler(`Bitte prüfen: ${falsch.join(", ")} ${falsch.length === 1 ? "ist keine gültige Adresse" : "sind keine gültigen Adressen"}.`);
      return;
    }
    if (!subject.trim() && !(await bestaetigen({ titel: "Ohne Betreff senden?", bestaetigen: "Trotzdem senden" }))) return;
    setZustand("sendet");
    setFehler(null);
    beendet.current = true;
    try {
      await sichern();
      await api.sendDraft(draft.id);
      toast.erfolg(to.length > 0 ? `Gesendet an ${to[0]}${alle.length > 1 ? ` und ${alle.length - 1} weitere` : ""}` : "Gesendet");
      onSent();
    } catch (err) {
      beendet.current = false;
      setFehler(fehlerText(err));
      setZustand("bereit");
    }
  };

  const schliessen = async () => {
    if (zustand === "sendet") return;
    beendet.current = true;
    // Unberührt und eben erst angelegt: kein leerer Entwurf in der Liste.
    if (draft && neuAngelegt.current && !geaendert && uploads.length === 0) {
      void api.deleteDraft(draft.id).catch(() => {});
      onClose();
      return;
    }
    if (draft && geaendert) {
      try {
        await sichern();
        toast("Entwurf gespeichert");
      } catch (err) {
        const trotzdem = await bestaetigen({
          titel: "Entwurf konnte nicht gespeichert werden",
          text: `${fehlerText(err)} — Beim Schließen gehen die letzten Änderungen verloren.`,
          bestaetigen: "Trotzdem schließen",
          abbrechen: "Weiter bearbeiten",
          gefahr: true,
        });
        if (!trotzdem) {
          beendet.current = false;
          return;
        }
      }
    }
    onClose();
  };

  const verwerfen = async () => {
    if (!draft) return onClose();
    const ok = await bestaetigen({
      titel: "Entwurf verwerfen?",
      text: "Text und hochgeladene Anhänge werden gelöscht.",
      bestaetigen: "Verwerfen",
      gefahr: true,
    });
    if (!ok) return;
    beendet.current = true;
    try {
      await api.deleteDraft(draft.id);
      toast("Entwurf verworfen");
      onClose();
    } catch (err) {
      beendet.current = false;
      toast.fehler(err);
    }
  };

  useDialogFokus(box, () => void schliessen());

  const arbeitet = zustand === "sendet" || zustand === "schreibt";
  const titel = TITEL[draft?.kind ?? kind] ?? TITEL[kind];

  return (
    <div
      className="sheet-backdrop"
      // Nur ein Klick, der auf dem Hintergrund beginnt, schließt. Wer im Text
      // markiert und daneben loslässt, will den Dialog nicht zumachen.
      onMouseDown={(e) => { druckDaneben.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (druckDaneben.current && e.target === e.currentTarget) void schliessen(); }}
    >
      <div
        ref={box}
        className="sheet composer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="composer-titel"
        onKeyDown={(e) => {
          // Strg/Cmd+Enter sendet, wie in jedem Mailprogramm.
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void senden();
          }
        }}
      >
        <div className="sheet-head">
          <span className="pane-title" id="composer-titel">{titel}</span>
          {draft?.source === "llm" && draft.model && <span className="chip">vorgeschrieben von {draft.model}</span>}
          <div className="spacer" />
          <span className="sicherung" aria-live="polite" data-zustand={sicherung}>
            {sicherung === "sichert" ? "Speichert …" : sicherung === "gesichert" ? "Gespeichert" : sicherung === "fehler" ? "Nicht gespeichert" : ""}
          </span>
          <button type="button" className="icon-btn" onClick={() => void schliessen()} title="Schließen und Entwurf behalten (Esc)" aria-label="Schließen">
            <X aria-hidden="true" />
          </button>
        </div>

        <div
          className="sheet-body"
          data-zieht={zieht}
          onDragEnter={(e) => {
            if (!e.dataTransfer.types.includes("Files")) return;
            e.preventDefault();
            ziehZaehler.current++;
            setZieht(true);
          }}
          onDragOver={(e) => {
            if (!e.dataTransfer.types.includes("Files")) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = draft ? "copy" : "none";
          }}
          // Ein Zähler statt target-Vergleich: Beim Überfahren der Kinder
          // feuert dragleave ständig, der Hinweis würde sonst flackern.
          onDragLeave={() => { ziehZaehler.current = Math.max(0, ziehZaehler.current - 1); if (ziehZaehler.current === 0) setZieht(false); }}
          onDrop={(e) => {
            if (!e.dataTransfer.types.includes("Files")) return;
            e.preventDefault();
            ziehZaehler.current = 0;
            setZieht(false);
            void dateienHochladen(Array.from(e.dataTransfer.files));
          }}
        >
          {zieht && <div className="ablegen-hinweis"><Paperclip aria-hidden="true" /> Dateien hier ablegen</div>}
          {zustand === "laedt" ? (
            <div className="empty" style={{ height: 220 }} aria-busy="true">Entwurf wird vorbereitet …</div>
          ) : ladeFehler ? (
            <div className="empty" style={{ height: 220 }} role="alert">
              <strong>Entwurf konnte nicht geöffnet werden</strong>
              <span>{ladeFehler}</span>
            </div>
          ) : (
            <>
              {konten.length > 1 && draft && (
                <div className="adressfeld">
                  <label htmlFor="composer-von">Von</label>
                  <Select
                    id="composer-von"
                    value={String(draft.account_id)}
                    options={konten.map((k) => ({ value: String(k.id), label: k.name ? `${k.name} <${k.email}>` : k.email }))}
                    onChange={(v) => {
                      const accountId = Number(v);
                      const vorher = draft.account_id;
                      setDraft({ ...draft, account_id: accountId });
                      api.updateDraft(draft.id, { accountId }).catch((err) => {
                        setDraft((d) => (d ? { ...d, account_id: vorher } : d));
                        toast.fehler(err);
                      });
                    }}
                    aria-label="Absender"
                    style={{ flex: 1 }}
                  />
                </div>
              )}
              <div className="adress-zeile">
                <AdressFeld label="An" werte={to} onChange={aendern(setTo)} inputRef={anFeld} />
                {!kopienOffen && (
                  <button type="button" className="btn quiet klein kopien-auf" onClick={() => setKopienOffen(true)}>Cc / Bcc</button>
                )}
              </div>

              {kopienOffen && (
                <>
                  <AdressFeld label="Kopie" werte={cc} onChange={aendern(setCc)} />
                  <AdressFeld label="Blindkopie" werte={bcc} onChange={aendern(setBcc)} />
                </>
              )}

              <div className="adressfeld">
                <label htmlFor="betreff">Betreff</label>
                <input
                  id="betreff"
                  className="input"
                  value={subject}
                  onChange={(e) => aendern(setSubject)(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) { e.preventDefault(); textFeld.current?.focus(); } }}
                />
              </div>

              {(anhaenge.length > 0 || uploads.length > 0 || laedtHoch) && (
                <div className="anhang-zeile">
                  <Paperclip aria-hidden="true" />
                  <div className="anhang-pillen">
                    {anhaengeMit &&
                      anhaenge.map((a) => (
                        <span className="chip" key={`w-${a.messageId}-${a.index}`} title={a.filename}>
                          <span className="chip-text">{a.filename}</span> · {formatBytes(a.size)}
                          <button type="button" className="chip-weg" aria-label={`${a.filename} entfernen`}
                            onClick={() => aendern(setAnhaenge)(anhaenge.filter((x) => x !== a))}><X aria-hidden="true" /></button>
                        </span>
                      ))}
                    {uploads.map((u) => (
                      <span className="chip" key={`u-${u.id}`} style={{ ["--chip-color" as string]: "var(--accent)" }} title={u.filename}>
                        <span className="chip-text">{u.filename}</span> · {formatBytes(u.size)}
                        <button type="button" className="chip-weg" aria-label={`${u.filename} entfernen`}
                          onClick={async () => {
                            if (!draft) return;
                            try {
                              await api.removeUpload(draft.id, u.id);
                              setUploads((liste) => liste.filter((x) => x.id !== u.id));
                            } catch (err) {
                              toast.fehler(err);
                            }
                          }}><X aria-hidden="true" /></button>
                      </span>
                    ))}
                    {laedtHoch && <span className="chip laedt">Lädt {laedtHoch} …</span>}
                  </div>

                  {anhaenge.length > 0 && (
                    /* Beim Weiterleiten die Übernahme in einem Zug abwählbar —
                       einzeln wäre das bei vierzehn Dateien eine Zumutung. */
                    <label className="anhang-schalter">
                      <input type="checkbox" checked={anhaengeMit} onChange={(e) => aendern(setAnhaengeMit)(e.target.checked)} />
                      Ursprungsanhänge
                    </label>
                  )}
                </div>
              )}

              {draft?.reply_to_id && (draft.kind === "antwort" || draft.kind === "allen") && (
                <Ursprung messageId={draft.reply_to_id} vorgeschrieben={draft.source === "llm"} />
              )}

              {hilfeOffen && (
                <div className="formulier-hilfe">
                  <div className="formulier-kopf">
                    <div className="tabs" role="group" aria-label="KI-Hilfe">
                      <button type="button" className="tab" aria-pressed={modus === "anpassen"} onClick={() => setModus("anpassen")} disabled={!body.trim()}
                        title={body.trim() ? "Den vorhandenen Text nach deinem Hinweis überarbeiten" : "Erst gibt es noch keinen Text"}>
                        <Wand2 aria-hidden="true" /> Anpassen
                      </button>
                      <button type="button" className="tab" aria-pressed={modus === "formulieren"} onClick={() => setModus("formulieren")}>
                        <Sparkles aria-hidden="true" /> Neu formulieren
                      </button>
                    </div>
                  </div>
                  {modus === "anpassen" ? (
                    <>
                      <div className="schnell-wahl" role="group" aria-label="Schnell anpassen">
                        {SCHNELL.map(([label, hinweis]) => (
                          <button key={label} type="button" className="schalter" disabled={arbeitet || !body.trim()}
                            onClick={() => { setAnpassHinweis(hinweis); void formulieren("anpassen", hinweis); }}>
                            {label}
                          </button>
                        ))}
                      </div>
                      <textarea
                        id="anpassung"
                        className="input"
                        rows={2}
                        placeholder="Was soll anders werden? z. B. „Termin Donnerstag 10 Uhr vorschlagen“, „Preis nicht nennen“, „Dank für die schnelle Antwort ergänzen“"
                        value={anpassHinweis}
                        onChange={(e) => setAnpassHinweis(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            e.stopPropagation();
                            void formulieren("anpassen");
                          }
                        }}
                        disabled={arbeitet}
                      />
                      <div className="formulier-zeile">
                        <span className="hint">Der Rest des Entwurfs bleibt, wie er ist. Signatur und Zitat bleiben stehen.</span>
                        <button type="button" className="btn primary klein" onClick={() => void formulieren("anpassen")} disabled={arbeitet || !draft || !body.trim()}
                          title="Entwurf anpassen (Strg/Cmd + Enter)">
                          <Wand2 aria-hidden="true" />
                          {zustand === "schreibt" ? "Passt an …" : "Anpassen"}
                        </button>
                      </div>
                    </>
                  ) : (
                  <>
                  <label htmlFor="stichpunkte">
                    <Sparkles aria-hidden="true" />
                    Was soll drinstehen?
                  </label>
                  <textarea
                    ref={stichpunktFeld}
                    id="stichpunkte"
                    className="input"
                    rows={3}
                    placeholder={
                      istAntwort
                        ? "Stichpunkte oder Anweisung, z. B. „Termin Do 14 Uhr passt, Unterlagen folgen bis Freitag“ — leer lassen für einen freien Vorschlag"
                        : "Stichpunkte oder Anweisung, z. B. „Angebot nachfassen, freundlich, bis Ende Oktober Rückmeldung“"
                    }
                    value={stichpunkte}
                    onChange={(e) => setStichpunkte(e.target.value)}
                    onKeyDown={(e) => {
                      // Strg/Cmd+Enter formuliert hier, statt die Mail zu senden.
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        e.stopPropagation();
                        void formulieren("formulieren");
                      }
                    }}
                    disabled={arbeitet}
                  />
                  <div className="formulier-zeile">
                    <span className="hint">
                      Schon Text im Entwurf? Der wird mit ausformuliert. Signatur und Zitat bleiben stehen.
                    </span>
                    {istAntwort && (
                      <label className="anhang-schalter" title="Bilder (per Texterkennung) und PDF-Anhänge fließen in den Vorschlag ein. Bilder gehen dabei ungeschwärzt an den Modellanbieter.">
                        <input type="checkbox" checked={anhaengeLesen} onChange={(e) => anhaengeUmschalten(e.target.checked)} disabled={arbeitet} />
                        Anhänge mitlesen
                      </label>
                    )}
                    <button type="button" className="btn primary klein" onClick={() => void formulieren("formulieren")} disabled={arbeitet || !draft}
                      title="Text formulieren (Strg/Cmd + Enter)">
                      <Sparkles aria-hidden="true" />
                      {zustand === "schreibt" ? "Formuliert …" : "Formulieren"}
                    </button>
                  </div>
                  </>
                  )}
                </div>
              )}

              <textarea
                ref={textFeld}
                id="text"
                className="composer-text"
                aria-label="Nachricht"
                placeholder="Nachricht schreiben …"
                value={body}
                onChange={(e) => aendern(setBody)(e.target.value)}
              />

              {fehler && <p className="error" role="alert" style={{ whiteSpace: "pre-line" }}>{fehler}</p>}
            </>
          )}
        </div>

        <div className="sheet-foot">
          <button
            type="button"
            className="btn primary"
            disabled={arbeitet || zustand === "laedt" || !draft || Boolean(laedtHoch)}
            onClick={() => void senden()}
            title="Senden (Strg/Cmd + Enter)"
          >
            <Send aria-hidden="true" />
            {zustand === "sendet" ? "Wird gesendet …" : "Senden"}
          </button>
          <button
            type="button"
            className="btn"
            title="Datei anhängen — oder einfach in das Fenster ziehen"
            disabled={!draft || Boolean(laedtHoch)}
            onClick={() => dateiWahl.current?.click()}
          >
            <Paperclip aria-hidden="true" />
            <span className="nur-breit">{laedtHoch ? "Lädt hoch …" : "Anhängen"}</span>
          </button>
          <input
            ref={dateiWahl}
            type="file"
            hidden
            multiple
            onChange={async (e) => {
              const dateien = Array.from(e.target.files ?? []);
              e.target.value = "";
              await dateienHochladen(dateien);
            }}
          />

          <button
            type="button"
            className="btn"
            aria-expanded={hilfeOffen}
            aria-controls="stichpunkte"
            disabled={zustand === "laedt" || !draft}
            onClick={hilfeUmschalten}
            title="Text mit dem Sprachmodell anpassen oder aus Stichpunkten formulieren"
          >
            <Sparkles aria-hidden="true" />
            <span className="nur-breit">{zustand === "schreibt" ? "Schreibt …" : "KI-Hilfe"}</span>
          </button>
          <div className="spacer" />
          {draft && (
            <button type="button" className="icon-btn gefahr" onClick={() => void verwerfen()} disabled={arbeitet} title="Entwurf verwerfen" aria-label="Entwurf verwerfen">
              <Trash2 aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
